import {
  AdditionalQuestionFieldType,
  AdditionalQuestionRef,
  DashboardLicenseEditorDetail,
  DashboardLicenseFormDetail,
  DashboardLicenseListItem,
  DashboardThirdPartyLicenseRef,
  CatalogLicenseStatus as ApiRestLicenseStatus,
  LicenseDuration as ApiRestLicenseDuration,
  LicenseGenderRestriction as ApiRestLicenseGenderRestriction,
  LicenseType as ApiRestLicenseType,
  LicenseRef,
} from "@/api/__generated__/schemas";
import {
  fromApiDate,
  LicenseFragment,
  ThirdPartyLicenseFragment,
  LicenseQuestionFragment,
  ApiBillingAddress,
  LicenseType as ApiLicenseType,
  LicenseWithIssuedCountFragment,
  LicenseWithFederationFragment,
  LicenseWithPlatformsFragment,
  CatalogLicenseStatus as ApiLicenseStatus,
  LicenseDuration as ApiLicenseDuration,
} from "@/graphql";
import { mapToLeagueReference } from "@/mappers/league.mappers";
import { mapToOrganization, mapToOrganizationReference } from "@/mappers/organization.mappers";
import { mapToBasePlatform } from "@/mappers/platform.mappers";
import { mapToScopePair } from "@/mappers/scope.mappers";
import {
  License,
  LicenseEditorDetails,
  LicenseFormDetail,
  LicenseQuestion,
  LicenseQuestionRef,
  LicenseReference,
  LicenseStatus,
  ThirdPartyLicense,
  BillingAddress,
  StringQuestion,
  NumberQuestion,
  FileQuestion,
  SelectQuestion,
  LicenseGenderRestriction,
  LicenseDuration,
  LicenseAgeLimitType,
  LicenseAgeRestriction,
  LicenseListItem,
  LicenseType,
  LicenseWithIssuedCount,
  LicenseWithPlatforms,
  LicenseWithOrganization,
  Question,
  QuestionType,
  ThirdPartyLicenseReference,
  UserReference,
  Organization,
} from "@/types";

import { mapToCountryCodeNullable, mapToCurrencyCode } from "./common.mappers";
import { mapToQuestion } from "./question.mappers";
import { mapToSport, mapToSportRef } from "./sport.mappers";
import { mapToUserReference } from "./user.mappers";

export const mapToThirdPartyLicense = (fragment: ThirdPartyLicenseFragment): ThirdPartyLicense => ({
  id: fragment.id,
  title: fragment.title ?? null,
  organizationName: fragment.organizationName ?? null,
  organizationAbbreviation: fragment.organizationAbbreviation ?? null,
  type: fragment.type ?? null,
  sports: fragment.sports?.map(mapToSport) ?? [],
});

export const mapToThirdPartyLicenseReference = (
  ref: DashboardThirdPartyLicenseRef
): ThirdPartyLicenseReference => ({
  id: ref.id,
  title: ref.title ?? "",
  type: ref.type ?? null,
});

export const mapToLicense = (fragment: LicenseFragment): License => {
  const ageRestrictionFrom = mapToLicenseAgeRestriction(
    fragment.ageFromType ?? null,
    fragment.ageFrom ?? null
  );
  const ageRestrictionTo = mapToLicenseAgeRestriction(
    fragment.ageToType ?? null,
    fragment.ageTo ?? null
  );

  return {
    id: String(fragment.id),
    title: fragment.title ?? "",
    englishTitle: fragment.englishTitle ?? null,
    fee: fragment.fee ?? null,
    currency: fragment.currency ? mapToCurrencyCode(fragment.currency) : null,
    duration: mapToLicenseDuration(fragment.duration!),
    status: mapToLicenseStatus(fragment.status!),
    startDate: fragment.startDate ? fromApiDate(fragment.startDate) : null,
    endDate: fragment.endDate ? fromApiDate(fragment.endDate) : null,
    gender: fragment.gender ? mapToLicenseGenderRestriction(fragment.gender) : "anyone",
    partnerFee: fragment.partnerLicenseFee ?? null,
    tournatedFee: fragment.tournatedLicenseFee ?? null,
    processingFee: fragment.fee ?? null,
    translations: {
      "en-GB": fragment.englishTitle ?? null,
      "en-US": fragment.englishTitle ?? null,
    },
    isManagerPayingServiceFee: fragment.isManagerPayingServiceFee ?? false,
    isManagerPayingTournatedFee: fragment.isManagerPayingTournatedFee ?? false,
    isManagerPayingPartnerFee: fragment.isManagerPayingPartnerFee ?? false,
    isManagerPayingPlatformFee: fragment.isManagerPayingPlatformFee ?? false,
    isManagerPayingStripeFee: fragment.isManagerPayingStripeFee ?? false,
    isSendNotificationsToManager: fragment.isSendNotificationsToManager ?? false,
    isApprovalRequired: fragment.isApprovalRequired ?? false,
    isAutoRenew: fragment.isAutoRenew ?? false,
    nationality: fragment.nationality ? mapToCountryCodeNullable(fragment.nationality) : null,
    managerContactEmail: null,
    ageRestrictions: {
      from: ageRestrictionFrom,
      to: ageRestrictionTo,
    },
  };
};

export const mapToLicenseWithOrganization = (
  fragment: LicenseWithFederationFragment
): LicenseWithOrganization => ({
  organization: fragment.federation ? mapToOrganization(fragment.federation) : null,
});

export const mapToLicenseWithPlatforms = (
  fragment: LicenseWithPlatformsFragment
): LicenseWithPlatforms => ({
  platforms: fragment.platforms?.map(mapToBasePlatform) ?? [],
});

const mapToLicenseAgeRestriction = (
  ageLimitType: string | null,
  value: number | null
): LicenseAgeRestriction | null => {
  if (!ageLimitType || !value) return null;

  return {
    type: mapToLicenseAgeLimitType(ageLimitType),
    value: value,
  };
};

// GraphQL path: the legacy fragment exposes status as a free-form string.
export const mapToLicenseStatus = (apiStatus: string): LicenseStatus => {
  switch (apiStatus.toLowerCase()) {
    case "active":
      return "active";
    case "inactive":
      return "inactive";
    default:
      throw new Error(`Unexpected license status: "${apiStatus}"`);
  }
};

// REST path: the dashboard DTOs expose the proper `CatalogLicenseStatus` enum.
const LICENSE_STATUS_FROM_API: { [K in ApiRestLicenseStatus]: LicenseStatus } = {
  active: "active",
  inactive: "inactive",
};

export const mapToLicenseStatusFromApi = (status: ApiRestLicenseStatus): LicenseStatus =>
  LICENSE_STATUS_FROM_API[status];

export const mapToLicenseQuestion = (fragment: LicenseQuestionFragment): LicenseQuestion => {
  switch (fragment.question?.fieldType) {
    case "String":
      return {
        id: fragment.id,
        question: mapToQuestion(fragment.question!) as StringQuestion,
        answer: fragment.answer ?? null,
        createdAt: fromApiDate(fragment.createdAt),
        updatedAt: fromApiDate(fragment.updatedAt),
      };
    case "Number":
      return {
        id: fragment.id,
        question: mapToQuestion(fragment.question!) as NumberQuestion,
        answer: isNaN(Number(fragment.answer)) ? null : Number(fragment.answer),
        createdAt: fromApiDate(fragment.createdAt),
        updatedAt: fromApiDate(fragment.updatedAt),
      };
    case "File":
      return {
        id: fragment.id,
        question: mapToQuestion(fragment.question!) as FileQuestion,
        answer: fragment.answer ?? null,
        fileUrl: fragment.file ?? null,
        createdAt: fromApiDate(fragment.createdAt),
        updatedAt: fromApiDate(fragment.updatedAt),
      };
    case "Select":
      return {
        id: fragment.id,
        question: mapToQuestion(fragment.question!) as SelectQuestion,
        answer: fragment.answer ?? null,
        createdAt: fromApiDate(fragment.createdAt),
        updatedAt: fromApiDate(fragment.updatedAt),
      };
    default:
      throw new Error(`Unexpected license question field type: "${fragment.question?.fieldType}"`);
  }
};

export const mapToApiBillingAddress = (fragment: ApiBillingAddress): BillingAddress => ({
  country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
  city: fragment.city ?? null,
  address: fragment.address ?? null,
  postalCode: fragment.postalCode ?? null,
});

const apiLicenseGenderRestrictionMap: {
  [key in LicenseGenderRestriction]: ApiRestLicenseGenderRestriction;
} = {
  male: ApiRestLicenseGenderRestriction.male,
  female: ApiRestLicenseGenderRestriction.female,
  anyone: ApiRestLicenseGenderRestriction.anyone,
};

export const mapToApiLicenseGenderRestriction = (
  gender: LicenseGenderRestriction
): ApiRestLicenseGenderRestriction => apiLicenseGenderRestrictionMap[gender];

export const mapToLicenseGenderRestriction = (gender: string): LicenseGenderRestriction => {
  switch (gender.toLowerCase()) {
    case "male":
      return "male";
    case "female":
      return "female";
    case "anyone":
      return "anyone";
    case "other":
      return "anyone";
    default:
      throw new Error(`Unexpected license gender restriction: "${gender}"`);
  }
};

export const mapToApiLicenseAgeLimitType = (ageLimitType: LicenseAgeLimitType): string => {
  switch (ageLimitType) {
    case "year":
      return "year";
    case "age":
      return "age";
  }
};

export const mapToLicenseAgeLimitType = (ageLimitType: string): LicenseAgeLimitType => {
  switch (ageLimitType) {
    case "year":
      return "year";
    case "age":
      return "age";
    default:
      return "age";
  }
};

export const mapToLicenseDuration = (duration: string): LicenseDuration => {
  switch (duration) {
    case "ONE_MONTH":
    case "1 month":
      return "1month";
    case "3 months":
    case "THREE_MONTHS":
      return "3months";
    case "12 months":
    case "TWELVE_MONTHS":
      return "12months";
    case "UNTIL_END_OF_CURRENT_YEAR":
    case "Until end of current year":
      return "untilEndOfCurrentYear";
    case "SPECIFIC_PERIOD":
    case "Specific time":
      return "specificPeriod";
    case "ONE_TIME":
    case "1 time":
      return "1time";
    default:
      return "1month";
  }
};

const licenseTypeMap: { [key in ApiLicenseType]: LicenseType } = {
  [ApiLicenseType.License]: "license",
  [ApiLicenseType.Certificate]: "certificate",
  [ApiLicenseType.ApplicationForm]: "applicationForm",
};

export const mapToLicenseType = (type: ApiLicenseType): LicenseType => {
  return licenseTypeMap[type];
};

const licenseTypeMapReverse: { [key in LicenseType]: ApiLicenseType } = {
  license: ApiLicenseType.License,
  certificate: ApiLicenseType.Certificate,
  applicationForm: ApiLicenseType.ApplicationForm,
};

export const mapToApiLicenseType = (type: LicenseType): ApiLicenseType => {
  return licenseTypeMapReverse[type];
};

const licenseStatusMapReverse: { [key in LicenseStatus]: ApiLicenseStatus } = {
  active: ApiLicenseStatus.Active,
  inactive: ApiLicenseStatus.Inactive,
};

export const mapToApiLicenseStatus = (status: LicenseStatus): ApiLicenseStatus => {
  return licenseStatusMapReverse[status];
};

export const mapToApiLicenseStatusOld = (status: LicenseStatus): string => {
  switch (status) {
    case "active":
      return "active";
    case "inactive":
      return "inactive";
  }
};

export const mapToLicenseWithIssuedCount = (
  fragment: LicenseWithIssuedCountFragment
): LicenseWithIssuedCount => {
  return {
    issuedCount: fragment.issuedCount ?? 0,
  };
};

const licenseDurationMapReverse: { [key in LicenseDuration]: ApiLicenseDuration } = {
  "1month": ApiLicenseDuration.OneMonth,
  "3months": ApiLicenseDuration.ThreeMonths,
  "1time": ApiLicenseDuration.OneTime,
  "12months": ApiLicenseDuration.TwelveMonths,
  specificPeriod: ApiLicenseDuration.SpecificPeriod,
  untilEndOfCurrentYear: ApiLicenseDuration.UntilEndOfCurrentYear,
};

export const mapToApiLicenseDuration = (duration: LicenseDuration): ApiLicenseDuration => {
  return licenseDurationMapReverse[duration];
};

const restLicenseDurationMapReverse: { [key in LicenseDuration]: ApiRestLicenseDuration } = {
  "1month": ApiRestLicenseDuration["1_month"],
  "3months": ApiRestLicenseDuration["3_months"],
  "1time": ApiRestLicenseDuration["1_time"],
  "12months": ApiRestLicenseDuration["12_months"],
  specificPeriod: ApiRestLicenseDuration.Specific_time,
  untilEndOfCurrentYear: ApiRestLicenseDuration.Until_end_of_current_year,
};

export const mapToRestApiLicenseDuration = (duration: LicenseDuration): ApiRestLicenseDuration =>
  restLicenseDurationMapReverse[duration];

const LICENSE_TYPE_FROM_API: { [K in ApiRestLicenseType]: LicenseType } = {
  LICENSE: "license",
  CERTIFICATE: "certificate",
  APPLICATION_FORM: "applicationForm",
};

export const mapToRestLicenseType = (type: ApiRestLicenseType): LicenseType =>
  LICENSE_TYPE_FROM_API[type];

const REST_LICENSE_TYPE_MAP_REVERSE: Record<
  LicenseType,
  "LICENSE" | "CERTIFICATE" | "APPLICATION_FORM"
> = {
  license: "LICENSE",
  certificate: "CERTIFICATE",
  applicationForm: "APPLICATION_FORM",
};

export const mapToRestApiLicenseType = (type: LicenseType) => REST_LICENSE_TYPE_MAP_REVERSE[type];

export const mapToLicenseReference = (input: LicenseRef): LicenseReference => ({
  id: input.id,
  title: input.title ?? "",
  type: input.type ? mapToRestLicenseType(input.type) : null,
  fee: input.fee ?? null,
  currency: input.currency ? mapToCurrencyCode(input.currency) : null,
  duration: input.duration ? mapToLicenseDuration(input.duration) : null,
  federationId: input.federationId != null ? String(input.federationId) : null,
  startDate: input.startDate ? new Date(input.startDate) : null,
  endDate: input.endDate ? new Date(input.endDate) : null,
  isApprovalRequired: input.isApprovalRequired ?? false,
});

const ADDITIONAL_QUESTION_FIELD_TYPE_TO_QUESTION_TYPE: Record<
  string,
  StringQuestion["type"] | NumberQuestion["type"] | FileQuestion["type"] | SelectQuestion["type"]
> = {
  Text: "string",
  Number: "number",
  File: "file",
  Select: "select",
};

export const mapToLicenseQuestionRef = (input: AdditionalQuestionRef): LicenseQuestionRef => {
  const questionType = input.fieldType
    ? ADDITIONAL_QUESTION_FIELD_TYPE_TO_QUESTION_TYPE[input.fieldType]
    : null;

  const baseQuestion = {
    id: input.id,
    title: input.question ?? "",
    isRequired: input.isMandatory ?? false,
  };

  let question: Question;
  switch (questionType) {
    case "number":
      question = { ...baseQuestion, type: "number" };
      break;
    case "file":
      question = { ...baseQuestion, type: "file" };
      break;
    case "select": {
      const options = input.descriptor?.fieldType === "Select" ? input.descriptor.options : [];
      question = { ...baseQuestion, type: "select", options };
      break;
    }
    default:
      question = { ...baseQuestion, type: "string" };
      break;
  }

  return {
    id: input.id,
    question,
    isMandatory: input.isMandatory ?? false,
    isHide: input.isHide ?? false,
    askOnlyOnce: input.askOnlyOnce ?? false,
    askEachAthlete: input.askEachAthlete ?? false,
  };
};

export const mapToLicenseFormDetail = (input: DashboardLicenseFormDetail): LicenseFormDetail => ({
  ...mapToLicenseReference({
    id: input.id,
    title: input.title,
    type: input.type,
    fee: input.fee,
    currency: input.currency,
    duration: input.duration,
    federationId: input.federationId,
    startDate: input.startDate,
    endDate: input.endDate,
    isApprovalRequired: input.isApprovalRequired,
  }),
  tournatedLicenseFee: input.tournatedLicenseFee ?? null,
  partnerLicenseFee: input.partnerLicenseFee ?? null,
  isManagerPayingPlatformFee: input.isManagerPayingPlatformFee ?? false,
  isSendNotificationsToManager: input.isSendNotificationsToManager ?? false,
  genderRestriction: input.genderRestriction
    ? mapToLicenseGenderRestriction(input.genderRestriction)
    : "anyone",
  nationality: input.nationality ? mapToCountryCodeNullable(input.nationality) : null,
  ageFrom: input.ageFrom ?? null,
  ageFromType: input.ageFromType ? mapToLicenseAgeLimitType(input.ageFromType) : null,
  ageTo: input.ageTo ?? null,
  ageToType: input.ageToType ? mapToLicenseAgeLimitType(input.ageToType) : null,
  federation: input.federation
    ? {
        id: input.federation.id,
        name: input.federation.organizationName ?? "",
        abbreviation: input.federation.organizationAbbreviation ?? null,
        logo: input.federation.logo ?? null,
      }
    : null,
  questions: input.additionalQuestions.map(mapToLicenseQuestionRef),
});

export const mapToLicenseListItem = (input: DashboardLicenseListItem): LicenseListItem => ({
  id: input.id,
  title: input.title ?? "",
  type: input.type ? mapToRestLicenseType(input.type) : null,
  status: input.status ? mapToLicenseStatusFromApi(input.status) : "active",
  duration: input.duration ? mapToLicenseDuration(input.duration) : "1month",
  startDate: input.startDate ? new Date(input.startDate) : null,
  endDate: input.endDate ? new Date(input.endDate) : null,
  fee: input.fee ?? null,
  currency: input.currency ? mapToCurrencyCode(input.currency) : null,
  partnerFee: input.partnerLicenseFee ?? null,
  tournatedFee: input.tournatedLicenseFee ?? null,
  isApprovalRequired: input.isApprovalRequired ?? false,
  isAutoRenew: input.isAutoRenew ?? false,
  issuedCount: input.issuedCount,
  federation: input.federation ? mapToOrganizationReference(input.federation) : null,
  scopes: input.scopes.map(mapToScopePair),
  sports: input.sports.map(mapToSportRef),
});

const mapToLicenseEditorDetailManager = (
  input: NonNullable<DashboardLicenseEditorDetail["licenseManager"]>
): UserReference => mapToUserReference(input);

/**
 * Build a minimum-viable `Organization` from the slim federation ref returned
 * by the editor-detail endpoint. Only the `id` is consumed by the update view
 * today (passed to `LicenseGeneralFormCard` as `organizationId`); the rest are
 * placeholders so the type-check stays honest.
 */
const mapToLicenseEditorDetailOrganization = (
  input: NonNullable<DashboardLicenseEditorDetail["federation"]>
): Organization => ({
  id: input.id,
  types: ["privateEntity"],
  name: input.organizationName ?? "",
  abbreviation: input.organizationAbbreviation ?? "",
  about: null,
  educationalInstitutionNumber: null,
  geoScope: "national",
  contactEmail: null,
  contactPhone: null,
  website: null,
  logoFile: null,
  legacyLogoUrl: input.logo ?? null,
  country: null,
  status: "active",
  enableLicenses: false,
  enableCertificates: false,
  enableApplicationForms: false,
  enableRankings: false,
  isClubForTeamRequired: false,
  allowNewsTagging: true,
  defaultTaggedNewsVisibility: "public",
  allowGalleryTagging: true,
  defaultTaggedGalleryVisibility: "public",
  createdAt: new Date(0),
  updatedAt: new Date(0),
  sports: [],
});

const additionalQuestionFieldTypeMap: { [key in QuestionType]: AdditionalQuestionFieldType } = {
  string: AdditionalQuestionFieldType.Text,
  number: AdditionalQuestionFieldType.Number,
  file: AdditionalQuestionFieldType.File,
  select: AdditionalQuestionFieldType.Select,
};

export const mapToApiLicenseAdditionalQuestionFieldType = (
  type: QuestionType
): AdditionalQuestionFieldType => additionalQuestionFieldTypeMap[type];

export const mapToLicenseEditorDetail = (
  input: DashboardLicenseEditorDetail
): LicenseEditorDetails => {
  const ageRestrictionFrom =
    input.ageFromType && input.ageFrom
      ? {
          type: mapToLicenseAgeLimitType(input.ageFromType),
          value: input.ageFrom,
        }
      : null;
  const ageRestrictionTo =
    input.ageToType && input.ageTo
      ? {
          type: mapToLicenseAgeLimitType(input.ageToType),
          value: input.ageTo,
        }
      : null;

  return {
    id: input.id,
    title: input.title ?? "",
    englishTitle: input.englishTitle ?? null,
    type: input.type ? mapToRestLicenseType(input.type) : null,
    fee: input.fee ?? null,
    currency: input.currency ? mapToCurrencyCode(input.currency) : null,
    duration: input.duration ? mapToLicenseDuration(input.duration) : "1month",
    status: input.status ? mapToLicenseStatusFromApi(input.status) : "active",
    startDate: input.startDate ? new Date(input.startDate) : null,
    endDate: input.endDate ? new Date(input.endDate) : null,
    gender: input.genderRestriction
      ? mapToLicenseGenderRestriction(input.genderRestriction)
      : "anyone",
    partnerFee: input.partnerLicenseFee ?? null,
    tournatedFee: input.tournatedLicenseFee ?? null,
    processingFee: input.fee ?? null,
    translations: Object.fromEntries(Object.entries(input.translations ?? {})),
    isManagerPayingServiceFee: input.isManagerPayingServiceFee ?? false,
    isManagerPayingTournatedFee: input.isManagerPayingTournatedFee ?? false,
    isManagerPayingPartnerFee: input.isManagerPayingPartnerFee ?? false,
    isManagerPayingPlatformFee: input.isManagerPayingPlatformFee ?? false,
    isManagerPayingStripeFee: input.isManagerPayingStripeFee ?? false,
    isSendNotificationsToManager: input.isSendNotificationsToManager ?? false,
    isApprovalRequired: input.isApprovalRequired ?? false,
    isAutoRenew: input.isAutoRenew ?? false,
    nationality: input.nationality ? mapToCountryCodeNullable(input.nationality) : null,
    managerContactEmail: input.managerContactEmail ?? null,
    ageRestrictions: {
      from: ageRestrictionFrom,
      to: ageRestrictionTo,
    },
    sports: input.sports.map(mapToSportRef),
    leagues: input.leagues.map(mapToLeagueReference),
    licenseManager: input.licenseManager
      ? mapToLicenseEditorDetailManager(input.licenseManager)
      : null,
    questions: input.additionalQuestions.map((ref) => mapToLicenseQuestionRef(ref).question),
    organization: input.federation
      ? mapToLicenseEditorDetailOrganization(input.federation)
      : mapToLicenseEditorDetailOrganization({
          id: "",
          organizationName: "",
          organizationAbbreviation: "",
          logo: null,
        }),
  };
};
